import * as HoverCard from "@radix-ui/react-hover-card";
import classNames from "classnames";
import copy from "copy-to-clipboard";
import moment from "moment";
import React, { useEffect } from "react";
import Duration from "~/components/shared/Duration";
import Icon from "~/components/shared/Icon";
import Spinner from "~/components/shared/Spinner";
import { DateLike, formatLogTimestamp } from "~/lib/date";
import parseEmoji from "~/lib/parseEmoji";
import JobLogLineStore from "~/stores/JobLogLineStore";

const LogHoverContext = React.createContext<{
  hoveredLine: number | null;
  setHoveredLine: React.Dispatch<React.SetStateAction<number | null>>;
  clearHoveredLine: () => void;
}>({
  hoveredLine: null,
  setHoveredLine: () => {
    return;
  },
  clearHoveredLine: () => {
    return;
  },
});

export { LogHoverContext };

export type LogSettingsContextType = {
  timestampsOn: boolean;
  timestampsInUTC: boolean;
};

const LogSettingsContext = React.createContext<LogSettingsContextType>({
  timestampsOn: false,
  timestampsInUTC: false,
});

export { LogSettingsContext };

export function Log({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  const [hoveredLine, setHoveredLine] = React.useState<number | null>(null);
  const clearHoveredLine = React.useCallback(() => setHoveredLine(null), []);

  const hoverContextValue = React.useMemo(
    () => ({ hoveredLine, setHoveredLine, clearHoveredLine }),
    [hoveredLine, setHoveredLine, clearHoveredLine],
  );

  return (
    <LogHoverContext.Provider value={hoverContextValue}>
      <div
        className={classNames("JobLogOutputComponent", className, {
          "flex min-h-0 flex-auto shrink-0 flex-col !rounded-none": Features.BuildSidebar,
        })}
        {...props}
      >
        {children}
      </div>
    </LogHoverContext.Provider>
  );
}

export function LogHeader({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={classNames("JobLogOutputComponent__Controls", className, {
        "z-10 !py-0": Features.BuildSidebar,
      })}
      {...props}
    >
      {children}
    </div>
  );
}

export function LogControl({
  children,
  active,
  ...props
}: (
  | React.ButtonHTMLAttributes<HTMLButtonElement>
  | ({ href: string } & React.AnchorHTMLAttributes<HTMLAnchorElement>)
) & {
  active?: boolean;
}) {
  const className = classNames(
    "JobLogOutputComponent__Button",
    { "JobLogOutputComponent__Button--Active": active },
    props.className,
  );

  if ("href" in props) {
    return (
      <a {...props} className={className}>
        {children}
      </a>
    );
  }

  return (
    <button {...props} className={className}>
      {children}
    </button>
  );
}

export function LogBody({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={classNames("JobLogOutputComponent__Body", className, {
        // adding a small gap at the top prevents the copy button from being clipped by
        // the log toolbar when it appears on the first line - z-index won't work as we're
        // in different stacking contexts
        "flex-auto pt-1": Features.BuildSidebar,
      })}
      {...props}
    >
      {children}
    </div>
  );
}

export function LogLoading({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={classNames("JobLogOutputComponent__Loader", className)} {...props}>
      {children}
    </div>
  );
}

export function LogMessage({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={classNames("p-4", className)} {...props}>
      {children}
    </div>
  );
}

export function LogContent({
  className,
  children,
  totalLines,
  ...props
}: React.HTMLAttributes<HTMLTableElement> & {
  totalLines?: number;
}) {
  const lineNumberWidth = totalLines?.toString().length ?? 0;

  const style = {
    "--line-number-width": `${Math.max(lineNumberWidth, 2)}ch`,
  } as React.CSSProperties;

  return (
    <table
      className={classNames("JobLogOutputComponent__Table", className)}
      style={style}
      {...props}
    >
      {children}
    </table>
  );
}

export function LogGroup({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <tbody className={className} {...props}>
      {children}
    </tbody>
  );
}

export const LogGroupHeader = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement> & {
    highlighted?: boolean;
    deemphasized?: boolean;
  }
>(({ className, highlighted, deemphasized, children, ...props }, ref) => {
  const innerRef = useScrollToHighlighted(highlighted);

  return (
    <tr
      ref={ref ?? innerRef}
      className={classNames(
        "JobLogOutputComponent__Header hover:z-[2]",
        {
          "JobLogOutputComponent__Header--Highlighted z-[1]": highlighted,
          "JobLogOutputComponent__Header--Deemphasized": deemphasized,
        },
        className,
      )}
      {...props}
    >
      {children}
    </tr>
  );
});

export function LogGroupName({
  className,
  name,
  expanded,
  ...props
}: React.TdHTMLAttributes<HTMLTableCellElement> & {
  name: string;
  expanded?: boolean;
}) {
  return (
    <td className={classNames("JobLogOutputComponent__Header__Name", className)} {...props}>
      <span className="float-left -ml-1 mr-1 w-[2ch] py-px">
        <Icon
          icon={expanded ? "heroicons/16/solid/chevron-down" : "heroicons/16/solid/chevron-right"}
          className="block h-4 w-4"
        />
      </span>

      <span
        dangerouslySetInnerHTML={{
          __html: parseEmoji(name, { escape: false }),
        }}
      />
    </td>
  );
}

function useScrollToHighlighted(highlighted?: boolean) {
  const ref = React.useRef<HTMLTableRowElement>(null);

  useEffect(() => {
    // When feature flag is ON, scrolling is handled at the page level to prevent race conditions
    if (Features.RefactorLogScrollBehaviour) {
      return;
    }

    if (highlighted && ref.current) {
      ref.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, []);

  return ref;
}

export const LogLine = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement> & {
    highlighted?: boolean;
  }
>(({ className, highlighted, children, ...props }, ref) => {
  const innerRef = useScrollToHighlighted(highlighted);

  return (
    <tr
      ref={ref ?? innerRef}
      className={classNames(
        "JobLogOutputComponent__Line hover:z-[2]",
        { "JobLogOutputComponent__Line--Highlighted z-[1]": highlighted },
        className,
      )}
      {...props}
    >
      {children}
    </tr>
  );
});

const stopPropagation = (event: React.MouseEvent) => event.stopPropagation();

export function LogLineContent({
  className,
  children,
  ...props
}: React.TdHTMLAttributes<HTMLTableCellElement>) {
  return (
    <td className={classNames("JobLogOutputComponent__Line__Content", className)} {...props}>
      {children}
    </td>
  );
}

export function LogLineNumber({
  className,
  number,
  highlighted,
  copyButton,
  showCopyButton,
  jobId,
  ...props
}: React.TdHTMLAttributes<HTMLTableCellElement> & {
  number: number;
  highlighted?: boolean;
  copyButton?: React.ReactNode;
  showCopyButton?: boolean;
  jobId: string;
}) {
  const { hoveredLine, setHoveredLine } = React.useContext(LogHoverContext);
  const isInRange = JobLogLineStore.isLineInRange(jobId, number);

  // Determine copy button visibility based on feature flags:
  // - With LogViewerCopyLinesAction: only show on selected lines (no hover)
  // - Without: show on selected lines or on line hover
  let isCopyButtonVisible: boolean;
  if (Features.LogViewerCopyLinesAction) {
    isCopyButtonVisible = JobLogLineStore.shouldShowCopyButton(jobId, number);
  } else {
    // Show on hover or when highlighted with no hover
    const isFirstLineOfRange = showCopyButton;
    const isAnotherLineHovered = hoveredLine !== null && hoveredLine !== number;
    const isHovered = hoveredLine === number;
    const isHighlightedWithNoHover = highlighted && hoveredLine === null;

    if (isFirstLineOfRange) {
      // Show unless another line is being hovered
      isCopyButtonVisible = !isAnotherLineHovered;
    } else if (isInRange) {
      isCopyButtonVisible = false;
    } else {
      isCopyButtonVisible = Boolean(isHovered || isHighlightedWithNoHover);
    }
  }

  const updateHoverLine = React.useCallback(
    (open: boolean) => {
      // Lines inside a range (without showCopyButton) don't participate in hover state changes
      if (isInRange && !showCopyButton) {
        return;
      }

      if (open) {
        setHoveredLine(number);
      } else {
        setHoveredLine((current) => (current === number ? null : current));
      }
    },
    [number, setHoveredLine, isInRange, showCopyButton],
  );

  return (
    <HoverCard.Root
      openDelay={0}
      closeDelay={0}
      open={highlighted && hoveredLine === null ? true : undefined}
      onOpenChange={updateHoverLine}
    >
      <HoverCard.Trigger asChild={true}>
        <td
          className={classNames(
            "JobLogOutputComponent__Number relative w-[--line-number-width] scroll-mt-20 pl-10",
            className,
          )}
          data-line-number={number}
          {...props}
        >
          {isCopyButtonVisible && copyButton && (
            <div className="absolute -top-[3px] left-2.5 z-50" onClick={stopPropagation}>
              {copyButton}
            </div>
          )}
        </td>
      </HoverCard.Trigger>
    </HoverCard.Root>
  );
}

export function LogTimestamp({
  className,
  time,
  utc,
  ...props
}: React.TdHTMLAttributes<HTMLTableCellElement> & {
  time?: DateLike | null;
  utc?: boolean | null;
}) {
  const timestamp = time ? formatLogTimestamp(time, utc ?? false) : null;

  return (
    <td
      className={classNames(
        "JobLogOutputComponent__Timestamp",
        { "JobLogOutputComponent__Timestamp--UTC": utc },
        className,
      )}
      {...props}
    >
      {timestamp}
    </td>
  );
}

export function LogGroupTime({
  startedAt,
  finishedAt,
  finished,
  durationPercentage,
  className,
  ...props
}: React.TdHTMLAttributes<HTMLTableCellElement> & {
  startedAt?: DateLike | null;
  finishedAt?: DateLike | null;
  finished: boolean;
  durationPercentage?: number | null;
}) {
  // Set the width of the badge to it's percentage of time taken to run
  // the group
  let badgeStyle = {};

  if (durationPercentage) {
    badgeStyle = { minWidth: `${durationPercentage}%` };
  } else if (startedAt && !finishedAt) {
    badgeStyle = { width: "20px" };
  }

  // Only show the timing node if we have the full timing information. We
  // could do live countdowns, but because we only get new data every 5
  // seconds, the counter would go up to 6 seconds, we get new data, then it
  // drops to 2 seconds. So it looks funky town.
  return (
    <td className={classNames("JobLogOutputComponent__Time", className)} {...props}>
      {startedAt && (
        <span className="JobLogOutputComponent__Time__Badge text-nowrap" style={badgeStyle}>
          {finishedAt ? (
            <Duration.Short from={startedAt} to={finishedAt} />
          ) : finished ? null : (
            <Spinner color={false} size="8px" className="mx-auto py-0.5" />
          )}
        </span>
      )}
    </td>
  );
}
export function LogCopyPermalink({
  jobId,
  lineNumber,
  label,
}: {
  jobId: string;
  lineNumber: number;
  label: string;
}) {
  const [copied, setCopied] = React.useState(false);

  const handleClick = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      // Prevent the line from being unhighlighted
      event.stopPropagation();

      const url = new URL(window.location.href);

      // Check if we have a range highlighted
      if (Features.LogViewerMultiLineSelection) {
        const range = JobLogLineStore.getHighlightedRange(jobId);
        if (range && range.start !== range.end) {
          // Range format: #jobId/L{start}-L{end}
          url.hash = `${jobId}/L${range.start}-L${range.end}`;
          copy(url.toString());
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
          return;
        }
      }

      // Line-only format: #jobId/L{lineNumber}
      url.hash = `${jobId}/L${lineNumber}`;

      copy(url.toString());
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    },
    [jobId, lineNumber],
  );

  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={handleClick}
      className="btn btn-depth btn-small before:shadow-depth-150 no-title-tooltip relative flex h-6 w-6 items-center justify-center bg-white p-0"
    >
      {copied ? (
        <Icon icon="heroicons/16/solid/check" className="size-4 text-green-700" />
      ) : (
        <Icon icon="heroicons/16/solid/link" className="size-4" />
      )}
    </button>
  );
}
